import { Button, Stack, Typography, useTheme, Box } from "@mui/material";
import pluralize from "pluralize";
import { useEffect, useCallback, useRef } from "react";

import { Dialog } from "../Dialog";
import { TagInput, TagList, TagLegend } from "./components";
import { useRecentTags, useTagState } from "./hooks";
import { calculateEntityUpdates } from "./tagUtils";
import { TaggedRow, ManageTagsFormDialogProps, TagType } from "./types";

export function ManageTagsFormDialog<T extends TaggedRow>({
  selectedRows,
  getEntityId,
  tenantId,
  onClose,
  onCloseConfirmation,
  confirm,
  open,
  withConfirmation,
  showConfirmation,
  onSubmit,
  loading,
  entityName,
  localStorageKey,
  commonTags = [],
  TagPickerComponent,
}: ManageTagsFormDialogProps<T>) {
  const theme = useTheme();
  const tagInputRef = useRef<HTMLInputElement>(null);
  const { recentTags, addRecentTag } = useRecentTags(localStorageKey);
  const {
    state,
    handleAddTag,
    handleRemoveTag,
    handleRestoreTag,
    handlePromoteTag,
    handleRemoveAllTags,
    hasChanges,
  } = useTagState(selectedRows, {
    entityName,
    localStorageKey,
    commonTags,
    TagPickerComponent,
    getEntityId,
  });

  // Auto-focus tag input when dialog opens
  useEffect(() => {
    if (open) {
      setTimeout(() => {
        tagInputRef.current?.focus();
      }, 100);
    }
  }, [open]);

  // Save handler
  const handleSave = useCallback(async () => {
    const entityUpdates = calculateEntityUpdates<T>(state.selectedTags, selectedRows, getEntityId);

    // Save new tags to recent tags
    state.selectedTags.forEach((tag) => {
      if (tag.type === TagType.NEW) {
        addRecentTag(tag.name);
      }
    });

    // Only call onSubmit if there are actually entities to update
    if (entityUpdates.length > 0) {
      await onSubmit(entityUpdates);
    }
  }, [state.selectedTags, selectedRows, onSubmit, addRecentTag, getEntityId]);

  const handleClose = useCallback(() => {
    if (withConfirmation) {
      onCloseConfirmation();
    }
    onClose();
  }, [withConfirmation, onCloseConfirmation, onClose]);

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        title={`Manage Tags for ${selectedRows.length} ${pluralize(
          entityName,
          selectedRows.length
        )}`}
        maxWidth="md"
        primaryActionButtonProps={{
          onClick: () => {
            if (withConfirmation) {
              confirm();
            } else {
              handleSave();
            }
          },
          disabled: !selectedRows.length || !hasChanges,
          loading: loading,
          saveText: "Save",
        }}
        additionalPrimaryActionElements={[
          <Button
            key="remove-all"
            variant="text"
            onClick={handleRemoveAllTags}
            disabled={state.selectedTags.length === 0}
            sx={{ marginRight: "auto" }}
          >
            Remove All Tags
          </Button>,
        ]}
      >
        <Stack sx={{ width: 600 }}>
          {/* Add Tag Section */}
          <Box>
            <TagInput
              onAddTag={handleAddTag}
              inputRef={tagInputRef}
              commonTags={commonTags}
              recentTags={recentTags}
              selectedTags={state.selectedTags.map((t) => t.name)}
              tenantId={tenantId}
              TagPickerComponent={TagPickerComponent}
            />
          </Box>

          {/* Tags Display Section */}
          <Box sx={{ mt: 2, mb: 2 }}>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                minHeight: 60,
                alignItems: "flex-start",
                mt: 2,
              }}
            >
              <TagList
                selectedTags={state.selectedTags}
                removedTags={state.removedTags}
                onRemove={handleRemoveTag}
                onRestore={handleRestoreTag}
                onPromote={handlePromoteTag}
                theme={theme}
                entityName={entityName}
              />
            </Box>

            <TagLegend theme={theme} entityName={entityName} />
          </Box>
        </Stack>
      </Dialog>

      {withConfirmation && (
        <Dialog
          open={showConfirmation}
          onClose={onCloseConfirmation}
          title="Save Tag Changes?"
          primaryActionButtonProps={{
            saveText: "Save Changes",
            onClick: () => {
              handleSave();
              handleClose();
            },
          }}
        >
          <Typography>
            This will update tags for {selectedRows.length}{" "}
            {pluralize(entityName.toLowerCase(), selectedRows.length)}.
          </Typography>
          <Typography sx={{ mt: 1 }}>
            Only the tags shown in "Selected Tags" will remain on the{" "}
            {pluralize(entityName).toLowerCase()}. All other tags will be removed.
          </Typography>
        </Dialog>
      )}
    </>
  );
}
